import { getData, patchData, postData } from "@/request/api";

export type GuestIdType = "national_id" | "resident_permit" | "passport" | "gcc_id";

// Identity details required by Shomoos + the accommodation contract.
export interface GuestIdentity {
  guestIdType: GuestIdType | "";
  guestIdNumber: string;
  guestNationality: string; // ISO alpha-2
  guestBirthDate: string; // YYYY-MM-DD
  guestGender: "male" | "female" | "";
  guestPassportExpiry?: string; // passport only
  guestPassportCountry?: string; // passport only (ISO alpha-2)
}

// Saved identity from a previous booking — the ID number only ever arrives masked.
export interface SavedGuestIdentity extends Omit<GuestIdentity, "guestIdNumber"> {
  saved: boolean;
  guestIdNumberMasked?: string;
}

export interface CreateReservationPayload extends Partial<GuestIdentity> {
  propertyId: string;
  checkIn: string; // YYYY-MM-DD
  checkOut: string; // YYYY-MM-DD
  adults?: number;
  children?: number;
  promoCode?: string;
}

export interface CreateReservationResult {
  success: boolean;
  reservation: {
    id: string;
    checkIn: string;
    checkOut: string;
    nights: number;
    totalPrice: number;
    currency: string;
    status: string;
    holdExpiresAt?: string;
    holdMinutes?: number;
  };
  /** true = nothing to pay (100% promo code): confirmed already, payment is null */
  free?: boolean;
  payment: {
    clientSecret: string;
    intentionId: string;
    reference: string;
    checkoutUrl: string;
  } | null;
}

// Format a Date as local YYYY-MM-DD (no timezone shift).
export function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export const reservationsService = {
  async create(payload: CreateReservationPayload): Promise<CreateReservationResult> {
    // Authenticated route — userId comes from the session cookie (withCredentials).
    const res = await postData("reservations", payload);
    return res.data;
  },

  // Get a fresh Paymob checkout link for a held reservation ("Pay now").
  async guestProfile(): Promise<SavedGuestIdentity | null> {
    try {
      const res = await getData("reservations/guest-profile");
      return res.data?.data ?? null;
    } catch {
      return null;
    }
  },

  async payReservation(id: string): Promise<{ checkoutUrl: string }> {
    const res = await postData(`reservations/${id}/pay`, {});
    return res.data;
  },

  // Print-ready accommodation contract (opens in a new tab; cookies carry auth).
  // Same-origin (through the site's /api rewrite) so the login cookies are sent.
  contractUrl(id: string, { embed = false }: { embed?: boolean } = {}): string {
    return `/api/v1/reservations/${encodeURIComponent(id)}/contract${embed ? "?embed=1" : ""}`;
  },

  // Cancel my own held (unpaid) reservation.
  async cancel(id: string) {
    const res = await patchData(`reservations/${id}/cancel`, {});
    return res.data;
  },

  // My reservations (payment-based bookings).
  async myReservations(page = 1, limit = 20): Promise<{ data: any[]; pagination: any }> {
    try {
      const res = await getData("reservations/my", { page, limit });
      return res.data;
    } catch {
      return { data: [], pagination: { total: 0, page: 1, limit, totalPages: 0 } };
    }
  },

  // All blocked/held nights for a property (Zaaer + local holds) — YYYY-MM-DD[].
  async getBlockedDates(propertyId: string): Promise<string[]> {
    try {
      const res = await getData(`public/properties/${propertyId}/blocked-dates`);
      return res.data?.dates ?? [];
    } catch {
      return [];
    }
  },
};
